<script lang="ts">
    import { fade } from 'svelte/transition';

    function slide(_node: Element, { x = 0, duration = 250 }: { x?: number; duration?: number } = {}) {
        return {
            duration,
            css: (t: number) => {
                const eased = t < 0.5
                    ? 4 * t * t * t
                    : 1 - Math.pow(-2 * t + 2, 3) / 2;
                return `transform: translateX(${(1 - eased) * x}px); opacity: ${eased}`;
            },
        };
    }

    interface LightboxImage {
        src: string;
        alt?: string;
        caption?: string;
    }

    interface Props {
        images: LightboxImage[];
        index: number;
        open: boolean;
        onclose: () => void;
        oncaptionchange?: (index: number, value: string) => void;
    }

    let {
        images,
        index = $bindable(0),
        open = $bindable(false),
        onclose,
        oncaptionchange,
    }: Props = $props();

    const hasMultiple = $derived(images.length > 1);
    const current = $derived(images[index]);

    let triggerEl: HTMLElement | null = null;
    let direction = $state(0);
    let closing = $state(false);

    let triggerElements: HTMLElement[] = [];

    export function openFrom(i: number, element: HTMLElement) {
        // Collect all sibling thumbnail images for close-to-current support
        const container = element.closest('.grid');
        triggerElements = container
            ? Array.from(container.querySelectorAll('button img')) as HTMLElement[]
            : [element];

        if (document.startViewTransition) {
            element.style.viewTransitionName = 'lightbox-image';
            document.startViewTransition(() => {
                element.style.viewTransitionName = '';
                index = i;
                open = true;
                direction = 0;
                return tick();
            });
        } else {
            index = i;
            open = true;
            direction = 0;
        }
    }

    async function closeWithTransition() {
        const targetEl = triggerElements[index] ?? triggerElements[0] ?? null;

        if (document.startViewTransition && targetEl) {
            // Switch to the non-keyed rendering so the image has a stable
            // view-transition-name the API can snapshot.
            closing = true;
            await tick();

            const transition = document.startViewTransition(() => {
                open = false;
                targetEl.style.viewTransitionName = 'lightbox-image';
                return tick();
            });
            transition.finished.then(() => {
                targetEl.style.viewTransitionName = '';
                closing = false;
            });
        } else {
            onclose();
        }
    }

    function next() {
        if (index < images.length - 1) { direction = 1; index++; }
    }

    function prev() {
        if (index > 0) { direction = -1; index--; }
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            closeWithTransition();
        } else if (e.key === 'ArrowRight') {
            next();
        } else if (e.key === 'ArrowLeft') {
            prev();
        }
    }

    function handleBackdrop(e: MouseEvent) {
        if (e.target === e.currentTarget) {
            closeWithTransition();
        }
    }

    let touchStartX = 0;
    let touchStartY = 0;

    function handleTouchStart(e: TouchEvent) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
    }

    function handleTouchEnd(e: TouchEvent) {
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
            if (dx < 0) next();
            else prev();
        }
    }

    import { tick } from 'svelte';
</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open && current}
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
        onclick={handleBackdrop}
        ontouchstart={handleTouchStart}
        ontouchend={handleTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-label="Image preview"
        tabindex="-1"
    >
        {#if hasMultiple && index > 0}
            <button
                type="button"
                onclick={prev}
                class="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl backdrop-blur-sm transition-colors"
                aria-label="Previous image"
            >
                &#8249;
            </button>
        {/if}

        {#if closing}
            <div class="flex flex-col items-center max-h-[90vh] max-w-[90vw]">
                <img
                    src={current.src}
                    alt={current.alt ?? ''}
                    class="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
                    style="view-transition-name: lightbox-image"
                />
            </div>
        {:else}
            <div class="relative flex items-center justify-center max-h-[90vh] max-w-[90vw]">
                {#key index}
                    <div
                        class="flex flex-col items-center"
                        in:slide={{ x: direction * 800, duration: 250 }}
                        out:slide={{ x: direction * -800, duration: 250 }}
                        style="position: absolute;"
                    >
                        <img
                            src={current.src}
                            alt={current.alt ?? ''}
                            class="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
                            style="view-transition-name: lightbox-image"
                        />
                        {#if oncaptionchange}
                            <input
                                type="text"
                                value={current.caption ?? ''}
                                oninput={(e: Event) => oncaptionchange(index, (e.target as HTMLInputElement).value)}
                                onclick={(e: MouseEvent) => e.stopPropagation()}
                                onkeydown={(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); closeWithTransition(); } }}
                                placeholder="Caption (optional)"
                                class="mt-3 w-full max-w-[80vw] text-center text-base font-medium px-4 py-2 bg-black/60 rounded-lg backdrop-blur-sm text-white placeholder-white/40 border border-white/20 focus:border-cyan-400 focus:outline-none"
                            />
                        {:else if current.caption}
                            <p class="text-white text-base font-medium mt-3 text-center px-4 py-2 bg-black/60 rounded-lg backdrop-blur-sm max-w-[80vw]">{current.caption}</p>
                        {/if}
                    </div>
                {/key}
            </div>
        {/if}

        {#if hasMultiple && index < images.length - 1}
            <button
                type="button"
                onclick={next}
                class="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl backdrop-blur-sm transition-colors"
                aria-label="Next image"
            >
                &#8250;
            </button>
        {/if}

        {#if hasMultiple}
            <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">
                {index + 1} / {images.length}
            </div>
        {/if}

        <button
            type="button"
            onclick={closeWithTransition}
            class="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg backdrop-blur-sm transition-colors"
            aria-label="Close"
        >
            &times;
        </button>
    </div>
{/if}
